import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, User, Filter } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function LeadsList() {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    filterLeads();
  }, [leads, statusFilter, searchQuery]);

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API_URL}/leads?assigned_to=${user.email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
      setFilteredLeads(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei lead');
    } finally {
      setLoading(false);
    }
  };

  const filterLeads = () => {
    let filtered = [...leads];

    if (statusFilter !== 'all') {
      filtered = filtered.filter((lead) => lead.status === statusFilter);
    }

    if (searchQuery) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.phone.includes(searchQuery)
      );
    }

    setFilteredLeads(filtered);
  };

  const updateLeadStatus = async (newStatus) => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Stato aggiornato con successo');
      setSelectedLead(null);
      fetchLeads();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento dello stato');
    } finally {
      setUpdating(false);
    }
  };

  const updateLeadNotes = async (notes) => {
    if (!selectedLead) return;

    setUpdating(true);
    try {
      await axios.put(
        `${API_URL}/leads/${selectedLead.id}`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Note aggiornate con successo');
      setSelectedLead({ ...selectedLead, notes });
      fetchLeads();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento delle note');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      nuovo: 'Nuovo',
      telefonata_target: 'Telefonata Target',
      videochiamata: 'Videochiamata',
      convertito: 'Convertito',
      perso: 'Perso'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      nuovo: 'bg-blue-50 text-blue-700 border-blue-200',
      telefonata_target: 'bg-amber-50 text-amber-700 border-amber-200',
      videochiamata: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      convertito: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      perso: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">I Miei Lead</h1>
        <p className="text-slate-600">Gestisci e monitora i tuoi lead assegnati</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm mb-6 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Cerca per nome, email o telefono..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-11"
              data-testid="search-input"
            />
          </div>
          <div className="w-full md:w-64">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11" data-testid="status-filter">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4" />
                  <SelectValue placeholder="Filtra per stato" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="nuovo">Nuovo</SelectItem>
                <SelectItem value="telefonata_target">Telefonata Target</SelectItem>
                <SelectItem value="videochiamata">Videochiamata</SelectItem>
                <SelectItem value="convertito">Convertito</SelectItem>
                <SelectItem value="perso">Perso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        {filteredLeads.length === 0 ? (
          <Card className="bg-white border border-slate-200 shadow-sm p-12 text-center">
            <p className="text-slate-500">Nessun lead trovato con i filtri selezionati.</p>
          </Card>
        ) : (
          filteredLeads.map((lead) => (
            <Card
              key={lead.id}
              className="bg-white border border-slate-200 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all cursor-pointer p-6"
              onClick={() => setSelectedLead(lead)}
              data-testid="lead-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{lead.name}</h3>
                  <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      <span>{lead.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{lead.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(lead.status)}`}>
                    {getStatusLabel(lead.status)}
                  </span>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <Dialog open={selectedLead !== null} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl" data-testid="lead-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Dettaglio Lead</DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Nome</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <User className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.name}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Email</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.email}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Telefono</Label>
                  <div className="flex items-center gap-2 text-slate-900">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{selectedLead.phone}</span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Stato Attuale</Label>
                  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getStatusColor(selectedLead.status)}`}>
                    {getStatusLabel(selectedLead.status)}
                  </span>
                </div>
              </div>

              {/* Candidatura Section */}
              {selectedLead.candidatura && Object.keys(selectedLead.candidatura).length > 0 && (
                <div className="border-t border-slate-200 pt-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                    📋 Risposte alla Candidatura
                  </h3>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                    {Object.entries(selectedLead.candidatura).map(([question, answer], index) => (
                      <div key={index} className="border-b border-slate-200 last:border-0 pb-3 last:pb-0">
                        <p className="text-sm font-semibold text-slate-700 mb-1">{question}</p>
                        <p className="text-sm text-slate-900">{answer || 'Nessuna risposta'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Aggiorna Stato</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {['nuovo', 'telefonata_target', 'videochiamata', 'convertito', 'perso'].map((status) => (
                    <Button
                      key={status}
                      onClick={() => updateLeadStatus(status)}
                      disabled={updating || selectedLead.status === status}
                      variant={selectedLead.status === status ? 'default' : 'outline'}
                      className="text-xs"
                      data-testid={`status-button-${status}`}
                    >
                      {getStatusLabel(status)}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="notes" className="text-sm font-medium text-slate-700 mb-2 block">Note</Label>
                <Textarea
                  id="notes"
                  defaultValue={selectedLead.notes}
                  onBlur={(e) => {
                    if (e.target.value !== selectedLead.notes) {
                      updateLeadNotes(e.target.value);
                    }
                  }}
                  placeholder="Aggiungi note sul lead..."
                  rows={4}
                  className="resize-none"
                  data-testid="notes-textarea"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}